import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

const PRIVATE_CDN_PREFIX = "https://static.puntt.ai/";

export type SignedPdfUrlResult =
  | { status: "ok"; url: string }
  | { status: "not_ready" }
  | { status: "error" };

function isPrivateCdnUrl(url: string): boolean {
  return (
    url.startsWith(PRIVATE_CDN_PREFIX) &&
    !url.startsWith(`${PRIVATE_CDN_PREFIX}public/`)
  );
}

// why: the same hosts `public/fetch-sw.js` rewrites to the CDN; the signer accepts only the CDN form.
const PUNTT_ASSETS_ORIGINS = [
  "https://puntt-assets.s3.us-east-1.amazonaws.com/",
  "https://puntt-assets.s3.amazonaws.com/",
  "https://puntt-assets.s3-accelerate.amazonaws.com/",
  "https://s3.us-east-1.amazonaws.com/puntt-assets/",
  "https://s3.amazonaws.com/puntt-assets/",
];

/** A direct puntt-assets URL as the CDN serves it; any other URL as it is. */
export function privateCdnUrl(url: string): string {
  const origin = PUNTT_ASSETS_ORIGINS.find((prefix) => url.startsWith(prefix));
  if (origin == null || url.includes("X-Amz-Signature=")) {
    return url;
  }
  const key = decodeURIComponent(url.slice(origin.length).split("?")[0]);
  return `${PRIVATE_CDN_PREFIX}${key}`;
}

/**
 * Exchange a private asset URL for a short-lived presigned S3 GET URL that
 * pdf.js can fetch without needing the service-worker auth injection. A
 * direct puntt-assets URL is signed by its CDN form.
 *
 * Returns a discriminated result so callers can distinguish "file still
 * processing" (404 on the signed-url endpoint) from genuine load failures.
 */
export async function getSignedPdfUrlResult(
  url: string,
): Promise<SignedPdfUrlResult> {
  // why: a multipart upload is stored as S3's Location URL; fetched unsigned it is a 403 whenever the service worker is not controlling the tab.
  const cdnUrl = privateCdnUrl(url);
  if (!isPrivateCdnUrl(cdnUrl)) {
    return { status: "ok", url };
  }
  try {
    const result = (await smartFetch(
      `${API_URL}/private/catalyst/files/signed-url?url=${encodeURIComponent(cdnUrl)}`,
    )) as { url?: string } | null;
    if (result?.url) {
      return { status: "ok", url: result.url };
    }
    return { status: "error" };
  } catch (err) {
    const stillProcessing = err instanceof Response && err.status === 404;
    if (stillProcessing) {
      return { status: "not_ready" };
    }
    console.error("[getSignedPdfUrl] failed to obtain presigned URL", err);
    return { status: "error" };
  }
}

/**
 * Legacy helper — returns the signed URL string on success, or the original
 * URL when signing fails. Prefer getSignedPdfUrlResult when the caller needs
 * to distinguish not-ready files from other errors.
 */
export async function getSignedPdfUrl(url: string): Promise<string> {
  const result = await getSignedPdfUrlResult(url);
  if (result.status === "ok") {
    return result.url;
  }
  return url;
}

/** How long an opened asset's object URL is kept before it is released. */
const OPENED_ASSET_LIFETIME_MS = 60_000;

// why: a blob URL runs in this app's origin, so the type is ours, never the response's.
// An SVG or HTML attachment shown as itself would execute its script with the user's session.
const VIEWABLE_ASSET_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
]);

/** The bytes as a blob a tab may show: a raster image or a PDF as itself, anything else as a download. */
export function inertAssetBlob(blob: Blob): Blob {
  const type = blob.type.split(";")[0].trim().toLowerCase();
  return new Blob([blob], {
    type: VIEWABLE_ASSET_TYPES.has(type) ? type : "application/octet-stream",
  });
}

/**
 * Open an asset in a new tab. The tab has no service worker to add the
 * token, so a private asset is opened by a presigned URL. One the signer
 * refuses is fetched by this page, whose service worker does add the token,
 * and shown from memory; the tab closes when even that fails.
 */
export async function openPrivateAsset(url: string): Promise<void> {
  // why: opened before the await; a tab opened after it is a popup the browser blocks.
  const tab = window.open("", "_blank");
  if (tab == null) {
    return;
  }
  tab.opener = null;
  const signed = await getSignedPdfUrlResult(url);
  if (signed.status === "ok") {
    tab.location.href = signed.url;
    return;
  }
  // why-not the raw url: a private object opened in a tab with no token is a 403 page, every time.
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`asset fetch answered ${response.status}`);
    }
    const objectUrl = URL.createObjectURL(
      inertAssetBlob(await response.blob()),
    );
    tab.location.href = objectUrl;
    window.setTimeout(
      () => URL.revokeObjectURL(objectUrl),
      OPENED_ASSET_LIFETIME_MS,
    );
  } catch (err) {
    console.error("[openPrivateAsset] could not open the asset", err);
    tab.close();
  }
}
